// Drag-the-route rerouting on the scope (pointer events: mouse, touch, pen).
// Press on a filed plan's hub end and drag: the route rubber-bands to the
// pointer, snaps as a ghost to the nearest hub, and a tip next to the pointer
// says what releasing there would do. Release on another hub to reroute;
// anywhere else cancels. The decisions are pure, in src/core/routedrag.js.

import { DRAG_THRESHOLD, nearestHub, pickDragPlan, resolveDrop } from '../core/routedrag.js';
import { previewAdjustment, hubEndOf } from '../core/sim.js';
import { HUBS } from '../config/constants.js';
import { adjustmentVerdict } from './actionbar.js';
import { setText, setClass, setHidden } from './dom.js';

const TIP_OFFSET = 16; // px from the pointer to the tip

/**
 * deps: {
 *   canvas, tip (element inside the scope), renderer, sim,
 *   selectedPlanId() -> id | null,
 *   onGrab(id)            a drag started on this plan (select it)
 *   onCommit(id, hubIdx)  reroute via that hub
 *   coarse() -> bool      touch-sized hit areas
 * }
 */
export function attachRouteDrag({ canvas, tip, renderer, sim, selectedPlanId, onGrab, onCommit, coarse }) {
  let press = null;  // { pointerId, planId, start } -- pressed, not yet dragged
  let drag = null;   // { planId, pointer } -- being dragged
  let suppressClick = false;
  let cursor = '';

  const grabRadius = () => (coarse() ? 26 : 16);
  const dropRadius = () => (coarse() ? 64 : 48);
  const endPx = (p) => renderer.toPx(hubEndOf(p));
  const planOf = (id) => sim.plans.find((p) => p.id === id);

  function setCursor(c) {
    if (c !== cursor) { canvas.style.cursor = c; cursor = c; }
  }

  function grabbable(pt) {
    return sim.running ? pickDragPlan(sim.plans, pt, endPx, grabRadius(), selectedPlanId()) : null;
  }

  function finish() {
    press = null;
    drag = null;
    setHidden(tip, true);
    setCursor('');
  }

  canvas.addEventListener('pointerdown', (e) => {
    suppressClick = false; // a fresh press; nothing left over from an earlier drag
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const pt = renderer.toLocal(e.clientX, e.clientY);
    const id = grabbable(pt);
    if (id === null) return;
    press = { pointerId: e.pointerId, planId: id, start: pt };
    try { canvas.setPointerCapture(e.pointerId); } catch { /* pointer already gone */ }
  });

  canvas.addEventListener('pointermove', (e) => {
    const pt = renderer.toLocal(e.clientX, e.clientY);
    if (!press) { setCursor(grabbable(pt) !== null ? 'grab' : ''); return; }
    if (e.pointerId !== press.pointerId) return;
    if (!drag) {
      if (Math.hypot(pt.x - press.start.x, pt.y - press.start.y) < DRAG_THRESHOLD) return;
      drag = { planId: press.planId, pointer: pt };
      onGrab(press.planId);
      setCursor('grabbing');
    }
    e.preventDefault();
    drag.pointer = pt;
  });

  canvas.addEventListener('pointerup', (e) => {
    if (!press || e.pointerId !== press.pointerId) return;
    if (drag) {
      // a drag never doubles as a click-to-select
      suppressClick = true;
      const p = planOf(drag.planId);
      const hubIdx = nearestHub(drag.pointer, renderer.hubPoints(), dropRadius());
      if (p && sim.running) {
        const drop = resolveDrop(p, hubIdx);
        if (drop.commit) onCommit(p.id, drop.hubIdx);
      }
    }
    finish();
  });

  const cancel = () => { if (drag) suppressClick = true; finish(); };
  canvas.addEventListener('pointercancel', cancel);
  canvas.addEventListener('lostpointercapture', () => { if (press) cancel(); });

  // Registered before the game's click handler, so it runs first.
  canvas.addEventListener('click', (e) => {
    if (suppressClick) { suppressClick = false; e.stopImmediatePropagation(); }
  });

  function showTip(pointer, size, tone, text) {
    setHidden(tip, false);
    setClass(tip, `drag-tip ${tone}`);
    setText(tip, text);
    const x = Math.min(pointer.x + TIP_OFFSET, size.w - tip.offsetWidth - 6);
    const y = Math.min(pointer.y + TIP_OFFSET, size.h - tip.offsetHeight - 6);
    tip.style.transform = `translate(${Math.round(Math.max(6, x))}px, ${Math.round(Math.max(6, y))}px)`;
  }

  return {
    /**
     * Call once per frame. Returns the drag overlay for the renderer
     * ({ planId, fixed, pointer, hubIdx, commit, preview }) or null.
     */
    frame() {
      if (!drag) return null;
      const p = planOf(drag.planId);
      if (!p || !sim.running) { cancel(); return null; } // launched or shift over mid-drag
      const hubIdx = nearestHub(drag.pointer, renderer.hubPoints(), dropRadius());
      const { commit } = resolveDrop(p, hubIdx);
      const preview = commit ? previewAdjustment(sim, p.id, 'reroute', hubIdx) : null;
      const size = renderer.size();
      if (commit) {
        const v = adjustmentVerdict(p, preview);
        showTip(drag.pointer, size, v.tone, `Via ${HUBS[hubIdx].name}: ${v.text}`);
      } else if (hubIdx >= 0) {
        showTip(drag.pointer, size, 'neutral', `Already via ${HUBS[hubIdx].name}: release to cancel`);
      } else {
        showTip(drag.pointer, size, 'neutral', 'Release on another hub to reroute');
      }
      return {
        planId: p.id,
        fixed: p.hubEnd === 'from' ? p.to : p.from,
        pointer: drag.pointer,
        hubIdx,
        commit,
        preview,
      };
    },
  };
}
